import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TestResult, EqualLoudnessContour } from '../../types/index';
import { interpAt, axisValue } from '../../core/math-utils';

interface Props {
  results?: { [key: string]: TestResult[] };
  contours?: { [key: string]: EqualLoudnessContour[] };
  selectedEar?: 'left' | 'right' | 'both';
  selectedContour?: string;
  options?: {
    width?: number;
    height?: number;
    margin?: number;
    minFreq?: number;
    maxFreq?: number;
    minDb?: number;
    maxDb?: number;
    freqLines?: number[];
  };
}

/**
 * Helper: convert frequency (Hz) to log10 value used as X
 */
function freqToLogX(freq: number): number {
  return axisValue(freq, 'log');
}

/**
 * Helper: convert logX back to frequency
 */
function logXToFreq(logX: number): number {
  return Math.pow(10, logX);
}

export default function GraphChart({
  results = {},
  contours = {},
  selectedEar = 'both',
  selectedContour = 'none',
  options: opts = {},
}: Props) {
  const options = {
    width: 750,
    height: 250,
    margin: 40,
    minFreq: 20,
    maxFreq: 20000,
    minDb: -100,
    maxDb: 0,
    freqLines: [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000],
    ...opts,
  };

  const logMin = useMemo(() => axisValue(options.minFreq, 'log'), [options.minFreq]);
  const logMax = useMemo(() => axisValue(options.maxFreq, 'log'), [options.maxFreq]);

  // Prepare chart data for results (single line)
  const chartData = useMemo(() => {
    const earResults = results[selectedEar] || [];
    const visible = earResults.filter((r) => r.frequency >= options.minFreq);
    visible.sort((a, b) => a.frequency - b.frequency);

    return visible.map((p) => ({
      x: freqToLogX(p.frequency),
      frequency: p.frequency,
      db: p.db,
    }));
  }, [results, selectedEar, options.minFreq]);

  // Add sentinel/padded data so Y axis honor fixed range regardless of visible data
  const paddedData = useMemo(() => {
    // sentinel points at both ends of X range to force axis to include 0 and -100
    const sentinels: { x: number; db: number; frequency?: number }[] = [
      { x: logMin, db: 0, frequency: options.minFreq },
      { x: logMax, db: 0, frequency: options.maxFreq },
      { x: logMin, db: -100, frequency: options.minFreq },
      { x: logMax, db: -100, frequency: options.maxFreq },
    ];
    // Prepend sentinels so they affect axis calculation but keep chartData separate for plotting
    return [...sentinels, ...chartData];
  }, [chartData, logMin, logMax, options.minFreq, options.maxFreq]);

  // Prepare contour data (if selected)
  const contourChartData = useMemo(() => {
    if (!selectedContour || selectedContour === 'none' || !contours[selectedContour]) return null;
    const contour = contours[selectedContour];
    if (!contour || contour.length === 0) return null;

    // Follow same clipping/interpolation behavior as original renderer: start at minFreq
    let firstIndex = contour.findIndex((p) => p[0] >= options.minFreq);
    if (firstIndex === -1) return null;

    const plot: { x: number; db: number; frequency: number }[] = [];

    if (firstIndex > 0 && contour[firstIndex - 1][0] < options.minFreq) {
      const p1 = contour[firstIndex - 1];
      const p2 = contour[firstIndex];
      const interp = interpAt(options.minFreq, p1 as any, p2 as any, 'log', 'log');
      plot.push({ x: freqToLogX(options.minFreq), db: interp - 90, frequency: options.minFreq });
    } else if (contour[firstIndex] && contour[firstIndex][0] > options.minFreq) {
      plot.push({ x: freqToLogX(options.minFreq), db: contour[firstIndex][1] - 90, frequency: options.minFreq });
    }

    for (let i = firstIndex; i < contour.length; i++) {
      if (contour[i][0] >= options.minFreq) {
        plot.push({ x: freqToLogX(contour[i][0]), db: contour[i][1] - 90, frequency: contour[i][0] });
      }
    }

    return plot;
  }, [contours, selectedContour, options.minFreq]);

  // X axis ticks in log-space based on freqLines
  const xTicks = useMemo(
    () => options.freqLines.map((f) => freqToLogX(f)).filter((v) => v >= logMin && v <= logMax),
    [options.freqLines, logMin, logMax]
  );

  // Y axis domain fixed to [-100, 0] regardless of selected contour
  // Use ascending numeric domain and explicit ticks so 0 is rendered at the top and -100 at the bottom.
  const yDomain: [number, number] = [-100, 0];

  // Explicit Y ticks from -100 up to 0 (ascending) for consistent placement
  const yTicks = useMemo(() => {
    return [-100, -90, -80, -70, -60, -50, -40, -30, -20, -10, 0];
  }, []);

  return (
    <div style={{ width: '100%', height: options.height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={paddedData}
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        >
          <CartesianGrid stroke="#eee" strokeWidth={1} />
          <XAxis
            dataKey="x"
            type="number"
            domain={[logMin, logMax]}
            ticks={xTicks}
            tickFormatter={(val) => {
              try {
                const f = Math.round(logXToFreq(Number(val)));
                return `${f}`;
              } catch {
                return '';
              }
            }}
            tick={{ fontSize: 12 }}
            padding={{ left: 0, right: 0 }}
          />
          <YAxis
            dataKey="db"
            type="number"
            domain={yDomain}
            ticks={yTicks}
            allowDataOverflow={true}
            scale="linear"
            tickFormatter={(v) => `${v}`}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            labelFormatter={(label) => {
              // label is logX
              const freq = Math.round(logXToFreq(Number(label)));
              return `${freq} Hz`;
            }}
            formatter={(value: any, name: any, props: any) => {
              if (name === 'db' || name === 'value') {
                return [`${value} dB`, ''];
              }
              return [value, name];
            }}
          />

          {/* Results line */}
          {chartData && chartData.length > 0 && (
            <Line
              type="monotone"
              dataKey="db"
              name="Results"
              data={chartData}
              dot={false}
              stroke="red"
              strokeWidth={2}
              isAnimationActive={false}
            />
          )}

          {/* Contour line */}
          {contourChartData && contourChartData.length > 0 && (
            <Line
              type="monotone"
              dataKey="db"
              name="Contour"
              data={contourChartData}
              dot={false}
              stroke="blue"
              strokeWidth={1}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}